/**
 * OptionSelect — one control for a group of mutually-exclusive (or multi-)
 * choices that renders itself by option count:
 *   • ≤ 3 options → inline pill-chips (the RecPill look: rounded, primary border
 *     when nothing is picked, dimmed when a *different* option is selected).
 *   • > 3 options → a dropdown (DateField/LanguageMenu recipe: $bgLayout trigger,
 *     full-bleed Backdrop, absolute animated panel with a scrollable option list).
 *
 * `value` is always an array (single-select is simply max one entry). Single
 * selection replaces + closes; multi toggles a checkbox row and stays open.
 * `searchable` adds a filter input above the dropdown list (long lists: countries).
 * Used by the Recruitment form in app/(portal)/processes.tsx.
 */
import { useState } from 'react'
import { Platform, ScrollView } from 'react-native'
import { Stack, Text, XStack, YStack } from 'tamagui'

import { useApp } from '../i18n'
import { fold } from '../lib/format'
import { palette } from '../theme/palette'
import { Backdrop } from './Backdrop'
import { Chip } from './Chip'
import { FormInput } from './form'
import { Icon } from './Icon'
import { AnimatePresence } from './motion'

/** Ширина списка: не уже 240, но и не шире колонки (веб; на native — прежние 240). */
const MIN_WIDTH = (Platform.OS === 'web' ? 'min(240px, 100%)' : 240) as number

export type SelectOption = { value: string; label: string }

const normalize = (options: (string | SelectOption)[]): SelectOption[] =>
  options.map((o) => (typeof o === 'string' ? { value: o, label: o } : o))

export interface OptionSelectProps {
  options: (string | SelectOption)[]
  /** Selected values — single-select carries at most one entry. */
  value: string[]
  onChange: (next: string[]) => void
  /** Multi-select: rows get a checkbox and the panel stays open on pick. */
  multiple?: boolean
  placeholder?: string
  /** Highlight a still-empty required field with the navy accent border. */
  highlight?: boolean
  /** Force the dropdown look regardless of option count (for lists that will grow). */
  list?: boolean
  /** Dropdown only: filter input above the list (case- and diacritics-insensitive). */
  searchable?: boolean
}

export function OptionSelect({ options, value, onChange, multiple, placeholder, highlight, list, searchable }: OptionSelectProps) {
  const { t } = useApp()
  const opts = normalize(options)
  const [open, setOpen] = useState(false)
  const [q, setQ] = useState('')
  // Закрытие сбрасывает поиск: при новом открытии снова виден весь список.
  const close = () => {
    setOpen(false)
    setQ('')
  }

  const pick = (v: string) => {
    if (multiple) {
      onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v])
    } else {
      onChange([v])
      close()
    }
  }

  // ── ≤3 → pill chips (RecPill style: primary border when empty, dim otherwise) ──
  if (opts.length <= 3 && !list) {
    const anySelected = value.length > 0
    return (
      <XStack gap={8} flexWrap="wrap">
        {opts.map((o) => {
          const on = value.includes(o.value)
          return (
            <Chip key={o.value} on={on} dim={anySelected && !on} onPress={() => pick(o.value)}>
              {o.label}
            </Chip>
          )
        })}
      </XStack>
    )
  }

  // ── >3 → dropdown ──
  const selected = opts.filter((o) => value.includes(o.value))
  const needle = fold(q.trim())
  const shown = needle ? opts.filter((o) => fold(o.label).includes(needle)) : opts
  return (
    // maxWidth: без него ширина триггера = длина подписи — мульти-выбор из нескольких значений
    // вылезал за карточку на 375 (подбор, «Причина открытия»).
    // minWidth — «240, но не шире колонки»: в CSS min-width сильнее max-width, и в половине ряда
    // («Валюта» рядом с «Суммой» в договоре на телефоне) список вылезал за карточку (30.10.2026).
    <Stack position="relative" alignSelf="flex-start" minWidth={MIN_WIDTH} maxWidth="100%">
      <XStack
        onPress={() => (open ? close() : setOpen(true))}
        cursor="pointer"
        height={40}
        alignItems="center"
        gap={8}
        paddingHorizontal={11}
        backgroundColor="$bgLayout"
        borderWidth={1}
        borderColor={open || highlight ? '$primary' : '$borderColor'}
        borderRadius={7}
        role="button"
        aria-expanded={open}
      >
        <Text flex={1} minWidth={0} numberOfLines={1} fontSize={14} color={selected.length ? '$color' : '$muted'}>
          {selected.length ? selected.map((o) => o.label).join(', ') : placeholder}
        </Text>
        <Icon name="chevron-down" size={16} color="$muted" />
      </XStack>

      <AnimatePresence>
        {open && (
          <>
            <Backdrop onPress={close} />
            <YStack
              animation="quicker"
              enterStyle={{ opacity: 0, y: -6, scale: 0.97 }}
              exitStyle={{ opacity: 0, y: -6, scale: 0.97 }}
              opacity={1}
              y={0}
              scale={1}
              position="absolute"
              top={46}
              left={0}
              right={0}
              zIndex={50}
              maxHeight={searchable ? 336 : 280}
              overflow="hidden"
              borderWidth={1}
              borderRadius={10}
              backgroundColor="$bgElevated"
              borderColor="$borderSecondary"
              shadowColor={palette.shadow}
              shadowOpacity={0.18}
              shadowRadius={20}
              shadowOffset={{ width: 0, height: 14 }}
            >
              {searchable ? (
                <Stack padding={8} borderBottomWidth={1} borderColor="$borderSecondary">
                  <FormInput value={q} onChangeText={setQ} placeholder={t({ ru: 'Поиск…', en: 'Search…', az: 'Axtarış…' })} autoFocus />
                </Stack>
              ) : null}
              <ScrollView style={{ maxHeight: 280 }}>
                {shown.length === 0 ? (
                  <Text paddingHorizontal={12} paddingVertical={9} fontSize={13} color="$muted">
                    {t({ ru: 'Ничего не найдено', en: 'Nothing found', az: 'Heç nə tapılmadı' })}
                  </Text>
                ) : null}
                {shown.map((o) => {
                  const on = value.includes(o.value)
                  return (
                    <XStack
                      key={o.value}
                      onPress={() => pick(o.value)}
                      cursor="pointer"
                      alignItems="center"
                      gap={10}
                      paddingHorizontal={12}
                      paddingVertical={9}
                      backgroundColor={on ? '$primaryBg' : 'transparent'}
                      hoverStyle={{ backgroundColor: on ? '$primaryBg' : '$backgroundHover' }}
                      role="button"
                      aria-selected={on}
                    >
                      {multiple ? (
                        <Stack
                          width={18}
                          height={18}
                          borderRadius={4}
                          borderWidth={1.5}
                          borderColor={on ? '$primary' : '$borderColor'}
                          backgroundColor={on ? '$primary' : 'transparent'}
                          alignItems="center"
                          justifyContent="center"
                        >
                          {on ? <Icon name="check" size={12} color="$onPrimary" /> : null}
                        </Stack>
                      ) : null}
                      <Text flex={1} fontSize={13} fontWeight={on ? '600' : '400'} color={on ? '$primary' : '$color'}>
                        {o.label}
                      </Text>
                      {!multiple && on ? <Icon name="check" size={15} color="$primary" /> : null}
                    </XStack>
                  )
                })}
              </ScrollView>
            </YStack>
          </>
        )}
      </AnimatePresence>
    </Stack>
  )
}
